import { useId } from 'react'
import type { LocalDate } from '../../platform/localDate'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { type LinkTarget } from './cashflowScope'
import { categoryName, type CategoryMap } from './cashflowCategories'
import { untilDate } from './cashflowRules'
import type { Breakdown, Runway } from './cashflowWire'
import { RankedList, type RankRow } from './RankedList'

/**
 * Runway (M3-R8, R9) as its own card, tagged "As of today": it is about today's
 * cash, not the selected period. Every figure is the server's — no client
 * runway math — and the card is absent (not zero, not infinity) when the
 * server sends no runway or no months.
 */
function RunwayCard({ runway, today, currency }: { runway: Runway | null; today: LocalDate; currency: string }) {
  const titleId = useId()
  if (runway === null || runway.months === null) return null
  const months = runway.months
  return (
    <Panel role="group" aria-labelledby={titleId} className="cf-panel cf-runway-card">
      <div className="cf-panel__head">
        <h2 id={titleId} className="cf-panel__title">
          Runway
        </h2>
        <span className="cf-scope-tag">As of today</span>
      </div>
      <p className="cf-runway-card__figure">
        {months} {months === 1 ? 'month' : 'months'}
      </p>
      <p className="cf-runway-card__line">
        If income stopped today, <Money cents={runway.cashCents} currency={currency} display="whole" /> of cash
          covers your <Money cents={runway.avgMonthlyExpenseCents} currency={currency} display="whole" /> average
          monthly spend
          {runway.until !== null && (
            <>
              {' '}
              until <span className="cf-nowrap">{untilDate(runway.until, today)}</span>
            </>
          )}
        .
      </p>
      <p className="cf-panel__sub">Runway is about today&apos;s cash, so it stays the same whichever period you pick.</p>
    </Panel>
  )
}

/**
 * "Where it came from" (M3-R24): the selected period's income sources as
 * ranked rows — the payer, its category beneath — then the Runway card. The
 * subtitle is the server's `totals.incomeCents`, never a sum of the rows; no
 * share is drawn, since the server sends none for income. With no sources the
 * card says so in one line.
 */
export function WhereItCameFrom({
  breakdown,
  incomeCents,
  names,
  currency,
  today,
  rowLink,
}: {
  breakdown: Breakdown
  incomeCents: number
  names: CategoryMap
  currency: string
  /** `window.today`: the runway date drops the year when it matches. */
  today: LocalDate
  /** The detail path for an income row. */
  rowLink: (t: LinkTarget) => string | undefined
}) {
  const rows: RankRow[] = breakdown.incomeSources.map((s, i) => ({
    // A payer with no merchant has no id; its name and position are unique within one response.
    key: s.merchantId ?? `~${s.name}-${i}`,
    name: s.name,
    sub: s.categoryId === null ? null : categoryName(names, s.categoryId),
    amountCents: s.amountCents,
    sharePct: null,
    to: rowLink(
      s.merchantId === null ? { kind: 'payer', name: s.name, side: 'in' } : { kind: 'merchant', id: s.merchantId, side: 'in' },
    ),
  }))
  return (
    <div className="cf-came">
      <Panel className="cf-panel">
        <div className="cf-panel__head">
          <h2 className="cf-panel__title">Where it came from</h2>
          <p className="cf-panel__sub">
            <Money cents={incomeCents} currency={currency} display="whole" /> came in
          </p>
        </div>
        {rows.length > 0 ? (
          <RankedList label="Where it came from" rows={rows} currency={currency} showAllNoun="sources" shareColumn={false} />
        ) : (
          // No sources is said in words, never an empty list.
          <p className="cf-empty__body">Nothing came in this period.</p>
        )}
      </Panel>
      <RunwayCard runway={breakdown.runway} today={today} currency={currency} />
    </div>
  )
}
